import m, { type RouteDefs } from "mithril";
import {
  AboutPage,
  AssessmentPage,
  CollaborationPage,
  HazardsPage,
  HomePage,
  LandingPage,
  NotFoundPage,
  OverviewPage,
  PreparationPage,
  RoadmapPage,
  SettingsPage,
  SolutionsPage,
  TaxonomyPage,
} from "../components";
import { Layout } from "../components/layout";
import { type Page, Pages } from "../models/page";
import { cells } from "./meiosis";
import { t } from "./translations";

const hasSession = (s: { currentSessionId?: string }) => !!s.currentSessionId;
const isInviteeCollaborationMode = (s: {
  collaboration?: { invitePayload?: unknown };
}) => !!s.collaboration?.invitePayload;
const hasSessionAndStep =
  (step: number) =>
    (s: {
      currentSessionId?: string;
      catModel?: { data?: { enabledSteps?: number[] } };
      collaboration?: { invitePayload?: unknown };
    }) =>
      !isInviteeCollaborationMode(s) &&
      !!s.currentSessionId &&
      (!s.catModel?.data?.enabledSteps ||
        s.catModel.data.enabledSteps.includes(step));

class RoutingService {
  private pages!: ReadonlyArray<Page>;

  constructor() { }

  public init() {
    const routes: Page[] = [
      {
        id: Pages.LANDING,
        title: t("LANDING", "TITLE"),
        route: t("LANDING", "ROUTE"),
        visible: false,
        default: true,
        hasNavBar: false,
        component: LandingPage,
      },
      {
        id: Pages.HOME,
        icon: "dashboard",
        title: t("HOME", "TITLE"),
        route: "/dashboard",
        visible: (s) => !isInviteeCollaborationMode(s) && hasSession(s),
        component: HomePage,
      },
      {
        id: Pages.HAZARDS,
        title: t("HAZARDS", "TITLE"),
        icon: "warning",
        route: t("HAZARDS", "ROUTE"),
        visible: hasSessionAndStep(0),
        component: HazardsPage,
        step: 0,
      },
      {
        id: Pages.OVERVIEW,
        title: t("overview"),
        icon: "apps",
        route: t("overview_route"),
        visible: hasSessionAndStep(1),
        component: OverviewPage,
        step: 1,
        fullscreen: true,
      },
      {
        id: Pages.ASSESSMENT,
        title: t("assessment"),
        icon: "assessment",
        route: t("assessment_route"),
        visible: false, //hasSessionAndStep(1),
        component: AssessmentPage,
        step: 1,
      },
      {
        id: Pages.SOLUTIONS,
        title: t("SOLUTIONS", "TITLE"),
        icon: "lightbulb",
        route: t("SOLUTIONS", "ROUTE"),
        visible: hasSessionAndStep(2),
        component: SolutionsPage,
        step: 2,
      },
      {
        id: Pages.ROADMAP,
        title: t("ROADMAP", "TITLE"),
        icon: "timeline",
        route: t("ROADMAP", "ROUTE"),
        visible: hasSessionAndStep(3),
        component: RoadmapPage,
        step: 3,
      },
      {
        id: Pages.ABOUT,
        icon: "info",
        title: t("ABOUT", "TITLE"),
        route: t("ABOUT", "ROUTE"),
        visible: (s) => !isInviteeCollaborationMode(s),
        component: AboutPage,
      },
      {
        id: Pages.TAXONOMY,
        title: t("taxonomy"),
        icon: "book",
        route: t("taxonomy_route"),
        visible: (s) => !isInviteeCollaborationMode(s) && hasSession(s),
        component: TaxonomyPage,
      },
      {
        id: Pages.PREPARATION,
        title: t("preparation"),
        icon: "settings",
        iconClass: "blue-text",
        route: t("preparation_route"),
        visible: (s) =>
          !isInviteeCollaborationMode(s) &&
          hasSession(s) &&
          s.curUser !== undefined &&
          s.curUser !== "user",
        component: PreparationPage,
      },
      {
        id: Pages.SETTINGS,
        icon: "settings_suggest",
        iconClass: "blue-text",
        title: t("SETTINGS", "TITLE"),
        route: t("SETTINGS", "ROUTE"),
        visible: (s) =>
          !isInviteeCollaborationMode(s) &&
          hasSession(s) &&
          s.curUser === "admin",
        component: SettingsPage,
      },
      {
        id: Pages.COLLABORATE,
        title: t("COLLABORATE", "TITLE"),
        icon: "group",
        route: t("COLLABORATE", "ROUTE"),
        visible: (s) =>
          s.role === "facilitator" || isInviteeCollaborationMode(s),
        component: CollaborationPage,
      },
      {
        id: Pages.NOT_FOUND,
        title: t("PAGE_NOT_FOUND"),
        route: "/404",
        visible: false,
        component: NotFoundPage,
      },
    ];
    this.setList(routes);
  }

  public getList() {
    return this.pages;
  }

  public setList(list: Page[]) {
    this.pages = Object.freeze(list);
  }

  public get defaultRoute() {
    const dashboard = this.pages.filter((d) => d.default).shift();
    return dashboard ? dashboard.route : this.pages[0].route;
  }

  public route(
    page: Pages,
    query?: { [key: string]: string | number | undefined },
  ) {
    const dashboard = this.pages.filter((d) => d.id === page).shift();
    return dashboard
      ? "#!" + dashboard.route + (query ? "?" + m.buildQueryString(query) : "")
      : this.defaultRoute;
  }

  public href(page: Pages, params = "" as string | number) {
    const dashboard = this.pages.filter((d) => d.id === page).shift();
    return dashboard
      ? `#!${dashboard.route.replace(/:\w*/, "")}${params}`
      : this.defaultRoute;
  }

  public switchTo(
    page: Pages,
    params?: { [key: string]: string | number | undefined },
    query?: { [key: string]: string | number | undefined },
  ) {
    const dashboard = this.pages.filter((d) => d.id === page).shift();
    if (dashboard) {
      const url =
        dashboard.route + (query ? "?" + m.buildQueryString(query) : "");
      m.route.set(url, params);
    }
  }

  public routingTable() {
    return this.pages.reduce((p, c) => {
      p[c.route] =
        c.hasNavBar === false
          ? {
            render: () => {
              return m(c.component, { ...cells() });
            },
          }
          : {
            render: () => {
              const cell = cells();
              if (
                isInviteeCollaborationMode(cell.state) &&
                c.id !== Pages.COLLABORATE
              ) {
                return m(
                  Layout,
                  { ...cell },
                  m(CollaborationPage, { ...cell }),
                );
              }
              return m(Layout, { ...cell }, m(c.component, { ...cell }));
            },
          };
      return p;
    }, {} as RouteDefs);
  }
}

export const routingSvc: RoutingService = new RoutingService();
